import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { PortableText } from "@portabletext/react";
import client from "../sanityClient";

const POST_QUERY = `*[_type == "post" && slug.current == $slug][0] {
  _id,
  title,
  publishedAt,
  excerpt,
  body,
  "mainImageUrl": mainImage.asset->url,
  "authorName": author->name,
  "authorBio": author->bio,
  "authorImageUrl": author->image.asset->url,
  "categories": categories[]->title,
  "slug": slug.current
}`;

// Portable Text rendering components
const ptComponents = {
  block: {
    normal: ({ children }) => (
      <p className="text-gray-300 leading-relaxed mb-5">{children}</p>
    ),
    h2: ({ children }) => (
      <h2 className="text-2xl font-bold text-white mt-10 mb-4">{children}</h2>
    ),
    h3: ({ children }) => (
      <h3 className="text-xl font-semibold text-white mt-8 mb-3">{children}</h3>
    ),
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-emerald-500 pl-5 my-6 text-gray-400 italic">
        {children}
      </blockquote>
    ),
  },
  list: {
    bullet: ({ children }) => (
      <ul className="list-disc list-inside space-y-2 mb-5 text-gray-300">
        {children}
      </ul>
    ),
    number: ({ children }) => (
      <ol className="list-decimal list-inside space-y-2 mb-5 text-gray-300">
        {children}
      </ol>
    ),
  },
  marks: {
    strong: ({ children }) => (
      <strong className="text-white font-semibold">{children}</strong>
    ),
    em: ({ children }) => <em className="italic text-gray-300">{children}</em>,
    link: ({ children, value }) => (
      <a
        href={value?.href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-emerald-400 hover:text-emerald-300 underline underline-offset-2 transition-colors"
      >
        {children}
      </a>
    ),
  },
  types: {
    image: ({ value }) =>
      value?.asset?.url ? (
        <figure className="my-8">
          <img
            src={value.asset.url}
            alt={value.alt || ""}
            className="rounded-xl w-full object-cover"
          />
          {value.caption && (
            <figcaption className="text-center text-gray-500 text-sm mt-3">
              {value.caption}
            </figcaption>
          )}
        </figure>
      ) : null,
  },
};

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    client.fetch(POST_QUERY, { slug }).then((data) => {
      if (!data) setNotFound(true);
      else setPost(data);
      setLoading(false);
    });
  }, [slug]);

  // Set page title and meta description for SEO
  useEffect(() => {
    if (post) {
      document.title = `${post.title} – Diet Reg Blog`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute("content", post.excerpt || post.title);
    }
    return () => {
      document.title = "Diet Reg – AI Nutrition Tracker";
    };
  }, [post]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f1117] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#0f1117] flex flex-col items-center justify-center text-white gap-4">
        <h1 className="text-3xl font-bold">Post not found</h1>
        <Link to="/blog" className="text-emerald-400 hover:underline">
          ← Back to Blog
        </Link>
      </div>
    );
  }

  const date = post.publishedAt
    ? new Date(post.publishedAt).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  return (
    <div className="min-h-screen bg-[#0f1117] text-white">
      {/* Back link */}
      <div className="max-w-2xl mx-auto px-6 pt-10">
        <Link
          to="/blog"
          className="text-gray-500 hover:text-emerald-400 text-sm transition-colors flex items-center gap-1"
        >
          ← Blog
        </Link>
      </div>

      {/* Hero image */}
      {post.mainImageUrl && (
        <div className="max-w-2xl mx-auto px-6 mt-8">
          <img
            src={post.mainImageUrl}
            alt={post.title}
            className="w-full h-64 object-cover rounded-2xl"
          />
        </div>
      )}

      {/* Article */}
      <article className="max-w-2xl mx-auto px-6 py-10">
        {/* Categories */}
        {post.categories?.length > 0 && (
          <div className="flex gap-2 flex-wrap mb-4">
            {post.categories.map((cat) => (
              <span
                key={cat}
                className="text-xs bg-emerald-500/10 text-emerald-400 px-2 py-0.5 rounded-full border border-emerald-500/20"
              >
                {cat}
              </span>
            ))}
          </div>
        )}

        <h1 className="text-4xl font-bold leading-tight mb-6">{post.title}</h1>

        {/* Author + date */}
        <div className="flex items-center gap-3 mb-10 pb-8 border-b border-white/10">
          {post.authorImageUrl && (
            <img
              src={post.authorImageUrl}
              alt={post.authorName}
              className="w-10 h-10 rounded-full object-cover"
            />
          )}
          <div>
            {post.authorName && (
              <p className="text-sm font-medium text-white">
                {post.authorName}
              </p>
            )}
            {date && <p className="text-xs text-gray-500">{date}</p>}
          </div>
        </div>

        {/* Body */}
        {post.body && (
          <PortableText value={post.body} components={ptComponents} />
        )}
      </article>

      {/* CTA */}
      <div className="max-w-2xl mx-auto px-6 pb-24">
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-8 text-center">
          <h3 className="text-xl font-bold mb-2">
            Ready to track your nutrition?
          </h3>
          <p className="text-gray-400 text-sm mb-5">
            Use Diet Reg to log meals with AI, hit your protein goals, and build
            lasting habits.
          </p>
          <a
            href="/"
            className="inline-block bg-emerald-500 hover:bg-emerald-400 text-black font-semibold px-6 py-3 rounded-xl transition-colors"
          >
            Try Diet Reg Free →
          </a>
        </div>
      </div>
    </div>
  );
}
